// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the inbox stores each ClickUp update as one markdown line
// ("**Berno** moved this to **to be assigned**", "**Zylia** commented\n> …").
// These helpers read who did what back out of it, and fold a day's updates on
// the same task into one card.

import type {ClickUpNotification} from '@app/features/konvo_notifications/state/KonvoClickUpInbox';

export type ClickUpEventKind =
	| 'status'
	| 'comment'
	| 'assignees'
	| 'due'
	| 'priority'
	| 'created'
	| 'attached'
	| 'other';

export interface ClickUpInboxEvent {
	/** Who did it, when the line names someone. */
	actor: string | null;
	kind: ClickUpEventKind;
	/** The status a task moved to ("to be assigned"). */
	status: string | null;
	/** A comment's text. */
	quote: string | null;
	/** What happened, without the actor, markdown or quote ("updated the assignees"). */
	action: string;
}

const clean = (text: string) => text.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();

export function parseClickUpEvent(description: string): ClickUpInboxEvent {
	const [head = '', ...rest] = description.split(/\n>\s?/);
	const quote = rest.length ? clean(rest.join(' ')) : null;
	const actorMatch = /^\*\*(.+?)\*\*\s*/.exec(head);
	const actor = actorMatch ? actorMatch[1].trim() : null;
	const body = actorMatch ? head.slice(actorMatch[0].length) : head;
	const action = clean(body);
	const lower = action.toLowerCase();
	const statusMatch = /moved this to \*\*(.+?)\*\*/i.exec(body);
	let kind: ClickUpEventKind = 'other';
	if (statusMatch || lower.startsWith('moved this to')) kind = 'status';
	else if (lower.startsWith('commented')) kind = 'comment';
	else if (lower.includes('assignee') || lower.includes('assigned'))
		kind = lower.startsWith('created') ? 'created' : 'assignees';
	else if (lower.includes('due date')) kind = 'due';
	else if (lower.includes('priority')) kind = 'priority';
	else if (lower.startsWith('created')) kind = 'created';
	else if (lower.startsWith('attached')) kind = 'attached';
	return {
		actor,
		kind,
		status: statusMatch
			? statusMatch[1].trim()
			: kind === 'status'
				? action.replace(/^moved this to\s*/i, '') || null
				: null,
		quote,
		action,
	};
}

export interface ClickUpInboxCard {
	/** Stable across renders: the newest notification's id. */
	key: string;
	taskId: string;
	taskName: string;
	listName: string;
	listId: string | null;
	/** Newest first. */
	items: Array<ClickUpNotification>;
	latestAt: string;
	unread: boolean;
}

export interface ClickUpInboxDay {
	/** yyyy-mm-dd in local time. */
	key: string;
	date: Date;
	cards: Array<ClickUpInboxCard>;
}

function dayKey(date: Date): string {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Days newest first; within a day one card per task (in the order of its
 * newest update), holding every update on that task that day.
 */
export function groupClickUpInbox(items: Array<ClickUpNotification>): Array<ClickUpInboxDay> {
	const sorted = [...items].sort((a, b) => b.created_at.localeCompare(a.created_at));
	const days: Array<ClickUpInboxDay> = [];
	for (const item of sorted) {
		const date = new Date(item.created_at);
		const key = dayKey(date);
		let day = days[days.length - 1];
		if (!day || day.key !== key) {
			day = {key, date: new Date(date.getFullYear(), date.getMonth(), date.getDate()), cards: []};
			days.push(day);
		}
		const card = day.cards.find((entry) => entry.taskId === item.task_id);
		if (card) {
			card.items.push(item);
			card.unread = card.unread || (!item.is_read && !item.is_cleared);
		} else {
			day.cards.push({
				key: item.id,
				taskId: item.task_id,
				taskName: item.task_name,
				listName: item.list_name,
				listId: item.list_id ?? null,
				items: [item],
				latestAt: item.created_at,
				unread: !item.is_read && !item.is_cleared,
			});
		}
	}
	return days;
}

export interface ClickUpInboxRow {
	/** The newest of the folded items. */
	item: ClickUpNotification;
	/** Newest first; more than one when the same update came in again and again. */
	items: Array<ClickUpNotification>;
}

/**
 * One row per update, with the same update repeated back to back ("updated the description"
 * on every save) folded into one row. Comments always keep their own row.
 */
export function foldClickUpEvents(items: Array<ClickUpNotification>): Array<ClickUpInboxRow> {
	const rows: Array<ClickUpInboxRow> = [];
	for (const item of items) {
		const last = rows[rows.length - 1];
		const comment = item.description.includes('\n>') || !!item.pictures?.length;
		if (last && last.item.description === item.description && !comment) last.items.push(item);
		else rows.push({item, items: [item]});
	}
	return rows;
}

/** Initials for an actor name ("Markus Mulder" → "MM", "ClickBot" → "C"). */
export function actorInitials(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '?';
	const first = Array.from(words[0])[0] ?? '';
	const last = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? '') : '';
	return (first + last).toUpperCase();
}

/** A stable hue per name so the same person keeps the same avatar colour. */
export function actorHue(name: string): number {
	let hash = 0;
	for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) >>> 0;
	return hash % 360;
}

/** List names are not unique; only a task's actual list ID identifies its location. */
export function findClickUpInboxList<T extends {list: {id: string}}>(listId: string | null, lists: Array<T>): T | null {
	return listId ? (lists.find((entry) => entry.list.id === listId) ?? null) : null;
}
